import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import DownloadIcon from "@mui/icons-material/Download";
import DescriptionIcon from "@mui/icons-material/Description";
import GavelIcon from "@mui/icons-material/Gavel";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import HistoryIcon from "@mui/icons-material/History";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { renderAsync } from "../../../vendor/docx-preview.min";
import axios from "../../../config/axiosConfig";
import { fieldSx } from "../../egresos/components/formStyles";

const campoSx = fieldSx;
const ICONOS = { generacion_distribuida: GavelIcon, autorizacion_expresa: VerifiedUserOutlinedIcon };

// Bloque con título: barra de encabezado y cuerpo, como las secciones del resto del sistema
const Seccion = ({ titulo, nota, icono, children, sinCuerpo }) => (
  <Box sx={{ border: 1, borderColor: "divider", bgcolor: "background.paper", mb: 1.25, overflow: "hidden" }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, px: 1.25, py: 0.5, bgcolor: "action.hover", borderBottom: 1, borderColor: "divider", borderLeft: "3px solid", borderLeftColor: "primary.main" }}>
      {icono}
      <Typography sx={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase", color: "primary.main" }}>{titulo}</Typography>
      {nota && <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>{nota}</Typography>}
    </Box>
    {sinCuerpo ? children : <Box sx={{ p: 1.25 }}>{children}</Box>}
  </Box>
);
const num = (v) => { const n = Number(String(v ?? "").replace(/\s/g, "").replace(",", ".")); return String(v ?? "").trim() !== "" && Number.isFinite(n) ? n : null; };
const fmt = (n) => n.toLocaleString("es-BO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fechaHora = (v) => { const d = new Date(v); return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("es-BO", { dateStyle: "short", timeStyle: "short" }); };

// Baja un archivo recibido del servidor (el nombre viene en Content-Disposition)
const guardarArchivo = (resp, nombreDefecto) => {
  const cab = resp.headers?.["content-disposition"] || "";
  const crudo = (/filename="?([^";]+)"?/.exec(cab) || [])[1];
  let nombre = nombreDefecto;
  try { nombre = crudo ? decodeURIComponent(crudo) : nombreDefecto; } catch { nombre = crudo || nombreDefecto; }
  const url = URL.createObjectURL(resp.data);
  const a = document.createElement("a");
  a.href = url; a.download = nombre; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};

// Imprime un contrato personalizado en Word para el servicio: se elige la plantilla, se revisan los datos (vienen del cliente y del último
// documento) y se descarga el .docx ya lleno. Cada documento generado queda en el historial para volver a bajarlo.
export default function ImprimirContratoPanel({ codServicioCliente }) {
  const [plantillas, setPlantillas] = useState([]);
  const [plantilla, setPlantilla] = useState("");
  const [def, setDef] = useState(null); // { plantilla: { campos }, datos }
  const [datos, setDatos] = useState({});
  const [cargando, setCargando] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState("");
  const [historial, setHistorial] = useState([]);
  const [nombreArchivo, setNombreArchivo] = useState("");
  const [vista, setVista] = useState(null); // { blob } del documento en vista previa
  const [contenedor, setContenedor] = useState(null);
  const [viendo, setViendo] = useState(false);
  const [faltan, setFaltan] = useState([]); // claves de los campos obligatorios que están vacíos (se marcan en rojo)

  const cargarHistorial = useCallback(async () => {
    try { const { data } = await axios.get("/api/contrato-documento/historial", { params: { cod_servicio_cliente: codServicioCliente } }); setHistorial(data?.data ?? data ?? []); } catch { setHistorial([]); }
  }, [codServicioCliente]);

  useEffect(() => {
    axios.get("/api/contrato-documento/plantillas")
      .then(({ data }) => { const l = data?.data ?? data ?? []; setPlantillas(l); setPlantilla((p) => p || l[0]?.id || ""); })
      .catch(() => setError("No se pudieron cargar las plantillas de contrato."));
    cargarHistorial();
  }, [cargarHistorial]);

  useEffect(() => {
    if (!plantilla) return;
    let activo = true;
    setCargando(true); setError(""); setDef(null);
    axios.get("/api/contrato-documento/datos", { params: { cod_servicio_cliente: codServicioCliente, plantilla } })
      .then(({ data }) => { if (!activo) return; const d = data?.data ?? data; setDef(d); setDatos(d?.datos || {}); })
      .catch((e) => activo && setError(e?.response?.data?.message || "No se pudieron cargar los datos del cliente."))
      .finally(() => activo && setCargando(false));
    return () => { activo = false; };
  }, [plantilla, codServicioCliente]);

  useEffect(() => {
    if (!vista || !contenedor) return;
    contenedor.innerHTML = "";
    renderAsync(vista.blob, contenedor, null, { className: "docx-vista", inWrapper: true, breakPages: true, ignoreLastRenderedPageBreak: true, renderHeaders: false } /* sin membrete: el logo y la marca de agua son parte del encabezado */)
      .catch(() => setError("No se pudo mostrar la vista previa del documento."));
  }, [vista, contenedor]);

  const campos = useMemo(() => def?.plantilla?.campos || [], [def]);
  const grupos = useMemo(() => campos.reduce((acc, c) => { (acc[c.grupo] = acc[c.grupo] || []).push(c); return acc; }, {}), [campos]);
  const set = (k) => (e) => { setDatos((d) => ({ ...d, [k]: e.target.value })); setFaltan((f) => f.filter((x) => x !== k)); };

  // Aviso si los pagos no suman el precio total
  const aviso = useMemo(() => {
    if (plantilla !== "generacion_distribuida") return "";
    const total = num(datos.precio_total);
    if (total === null) return "";
    const suma = ["pago_reserva", "pago_inicial", "pago_segundo", "pago_saldo"].reduce((a, k) => a + (num(datos[k]) || 0), 0);
    if (num(datos.pago_saldo) === null) return "";
    return Math.abs(suma - total) > 0.005 ? `Los pagos suman Bs. ${fmt(suma)} y el precio total es Bs. ${fmt(total)}.` : "";
  }, [datos, plantilla]);

  // Revisa los campos obligatorios: si falta alguno, los marca, avisa junto al botón y lleva al primero. Devuelve true si está todo completo.
  const datosCompletos = () => {
    const vacios = campos.filter((c) => c.obligatorio && !String(datos[c.k] ?? "").trim());
    if (!vacios.length) { setFaltan([]); return true; }
    setVista(null); // la vista previa tapa los avisos: se cierra para mostrar qué falta
    setFaltan(vacios.map((c) => c.k));
    setError(`Falta completar: ${vacios.map((c) => c.label).join(", ")}`);
    setTimeout(() => document.getElementById(`cd-${vacios[0].k}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
    return false;
  };

  const generar = async () => {
    if (!datosCompletos()) return;
    setGenerando(true); setError("");
    try {
      const resp = await axios.post("/api/contrato-documento/generar", { cod_servicio_cliente: codServicioCliente, plantilla, datos, nombre_archivo: nombreArchivo }, { responseType: "blob" });
      guardarArchivo(resp, "contrato.docx");
      setVista(null);
      cargarHistorial();
    } catch (e) {
      // con responseType blob el mensaje de error viene como Blob
      let msg = "No se pudo generar el documento.";
      try { msg = JSON.parse(await e.response.data.text()).message || msg; } catch { /* se deja el mensaje general */ }
      setError(msg);
    } finally {
      setGenerando(false);
    }
  };

  const previsualizar = async () => {
    if (!datosCompletos()) return;
    setViendo(true); setError("");
    try {
      const resp = await axios.post("/api/contrato-documento/previsualizar", { plantilla, datos }, { responseType: "blob" });
      setVista({ blob: resp.data });
    } catch (e) {
      let msg = "No se pudo generar la vista previa.";
      try { msg = JSON.parse(await e.response.data.text()).message || msg; } catch { /* mensaje general */ }
      setError(msg);
    } finally {
      setViendo(false);
    }
  };

  const bajar = async (id) => {
    try { guardarArchivo(await axios.get(`/api/contrato-documento/${id}/descargar`, { responseType: "blob" }), "contrato.docx"); } catch { setError("No se pudo descargar el documento."); }
  };

  const cliente = datos.cliente_nombre || "";

  return (
    <Box sx={{ pt: 1 }}>
      {error && !def && <Alert severity="error" sx={{ mb: 1.25 }} onClose={() => setError("")}>{error}</Alert>}

      {/* Elegir el documento */}
      <Typography sx={{ fontSize: 12, fontWeight: 800, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.4, mb: 0.75 }}>¿Qué documento quieres imprimir?</Typography>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
        {plantillas.map((pl) => {
          const Icono = ICONOS[pl.id] || DescriptionIcon;
          const activa = pl.id === plantilla;
          return (
            <Box
              key={pl.id} role="button" tabIndex={0} onClick={() => setPlantilla(pl.id)} onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setPlantilla(pl.id)}
              sx={{
                flex: "1 1 300px", display: "flex", alignItems: "center", gap: 1.25, px: 1.5, py: 1, cursor: "pointer", border: 1.5,
                borderColor: activa ? "primary.main" : "divider", bgcolor: (t) => (activa ? alpha(t.palette.primary.main, 0.07) : "background.paper"),
                "&:hover": { borderColor: "primary.main" },
              }}
            >
              <Box sx={{ width: 38, height: 38, display: "grid", placeItems: "center", borderRadius: "50%", color: activa ? "primary.contrastText" : "primary.main", bgcolor: (t) => (activa ? t.palette.primary.main : alpha(t.palette.primary.main, 0.1)) }}>
                <Icono fontSize="small" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 800, lineHeight: 1.25 }}>{pl.nombre.split(" – ")[0]}</Typography>
                {pl.nombre.includes(" – ") && <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{pl.nombre.split(" – ")[1]}</Typography>}
              </Box>
            </Box>
          );
        })}
      </Box>

      {cargando && <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={24} /></Box>}

      {def && (
        <Alert severity="info" icon={false} sx={{ mb: 1.25, py: 0, fontSize: 12.5 }}>
          Los datos vienen de lo registrado del cliente{cliente ? <> (<b>{cliente}</b>)</> : null} y del último documento de este servicio. Revísalos y corrígelos: lo que escribas aquí es lo que se imprime.
        </Alert>
      )}

      {def && Object.entries(grupos).map(([grupo, lista]) => (
        <Seccion key={grupo} titulo={grupo}>
          <Grid container spacing={1}>
            {lista.map((c) => (
              <Grid key={c.k} item size={{ xs: 12, md: c.ancho || 6 }}>
                <TextField
                  id={`cd-${c.k}`} fullWidth size="small" label={c.label} value={datos[c.k] ?? ""} onChange={set(c.k)} required={!!c.obligatorio} error={faltan.includes(c.k)} sx={campoSx}
                  type={c.tipo === "fecha" ? "date" : "text"} slotProps={c.tipo === "fecha" ? { inputLabel: { shrink: true } } : undefined}
                  inputProps={c.tipo === "numero" || c.tipo === "dinero" ? { inputMode: "decimal" } : undefined}
                  helperText={faltan.includes(c.k) ? "Obligatorio" : c.ayuda || undefined}
                />
              </Grid>
            ))}
          </Grid>
        </Seccion>
      ))}

      {aviso && <Alert severity="warning" sx={{ mb: 1.25, py: 0 }}>{aviso}</Alert>}

      {historial.length > 0 && (
        <Seccion titulo={`Documentos generados (${historial.length})`} nota="de este servicio" icono={<HistoryIcon sx={{ fontSize: 16, color: "primary.main" }} />} sinCuerpo>
          <Table size="small" sx={{ "& .MuiTableCell-root": { py: 0.25, px: 1, fontSize: 12 } }}>
            <TableHead>
              <TableRow>{["Fecha", "Documento", "N.º", "Generado por", ""].map((h) => <TableCell key={h || "acc"} sx={{ fontWeight: 700, color: "text.secondary" }}>{h}</TableCell>)}</TableRow>
            </TableHead>
            <TableBody>
              {historial.map((h) => (
                <TableRow key={h.id} hover>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{fechaHora(h.fecha_registro)}</TableCell>
                  <TableCell>{h.plantilla_nombre}</TableCell>
                  <TableCell>{h.numero || "—"}</TableCell>
                  <TableCell>{h.usuario || "—"}</TableCell>
                  <TableCell align="right" sx={{ py: 0 }}>
                    <Tooltip title="Volver a descargar"><IconButton size="small" onClick={() => bajar(h.id)}><DownloadIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Seccion>
      )}

      {/* Barra de acción fija abajo: siempre a la vista aunque el formulario sea largo */}
      {def && (
        <Box sx={{ position: "sticky", bottom: -1, zIndex: 2, display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1.5, flexWrap: "wrap", py: 1, px: 1.25, mt: 1, bgcolor: "background.paper", border: 1, borderColor: error ? "error.main" : "divider", boxShadow: "0 -4px 10px rgba(0,0,0,0.06)" }}>
          {error && (
            <Alert severity="error" variant="filled" onClose={() => { setError(""); setFaltan([]); }} sx={{ flexBasis: "100%", py: 0, fontSize: 13, fontWeight: 700 }}>
              {error}
            </Alert>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ mr: "auto" }}>Revisa cómo queda el documento antes de descargarlo.</Typography>
          <Button variant="contained" startIcon={viendo ? <CircularProgress size={16} color="inherit" /> : <VisibilityOutlinedIcon />} onClick={previsualizar} disabled={viendo || generando} sx={{ textTransform: "none", fontWeight: 700 }}>
            Vista previa
          </Button>
        </Box>
      )}

      <Dialog open={Boolean(vista)} onClose={() => setVista(null)} maxWidth="lg" fullWidth PaperProps={{ sx: { height: "92vh" } }}>
        <DialogTitle sx={{ py: 1, fontSize: 15, fontWeight: 800 }}>
          Vista previa
          <Typography component="span" sx={{ ml: 1, fontSize: 12, fontWeight: 400, color: "text.secondary" }}>Así se verá el documento; todavía no se descargó ni se guardó</Typography>
        </DialogTitle>
        {error && <Alert severity="error" variant="filled" onClose={() => setError("")} sx={{ borderRadius: 0, py: 0, fontWeight: 700 }}>{error}</Alert>}
        <DialogContent dividers sx={{ p: 0, bgcolor: "#e8eaed" }}>
          <Box ref={setContenedor} sx={{ "& .docx-vista-wrapper": { bgcolor: "#e8eaed", p: 1.5 }, "& .docx-vista-wrapper > section.docx-vista": { boxShadow: "0 1px 6px rgba(0,0,0,0.25)", mb: 1.5 } }} />
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1, gap: 1, flexWrap: "wrap" }}>
          <TextField
            size="small" label="Nombre del archivo (opcional)" value={nombreArchivo} onChange={(e) => setNombreArchivo(e.target.value)} placeholder="Ej. Contrato Juan Pérez"
            sx={{ ...campoSx, width: 320, mr: "auto" }} InputProps={{ endAdornment: <Typography variant="caption" color="text.secondary">.docx</Typography> }}
          />
          <Button onClick={() => setVista(null)} sx={{ textTransform: "none" }}>Seguir editando</Button>
          <Button variant="contained" startIcon={generando ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />} disabled={generando}
            onClick={generar} sx={{ textTransform: "none", fontWeight: 700 }}>
            Generar y descargar Word
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
